import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'

import { ResultCode } from '@/enums'
import { storage } from '@/utils/storage'
import type { ApiResult } from '@/types/api'

/**
 * Axios 实例 + 拦截器。
 *
 * 后端统一返回 { code, message, data, timestamp }。
 * - code === 0 : 成功，自动解包返回 data
 * - code !== 0 : 业务错误，Toast 提示 + reject
 * - 1007       : 登录失效，清除登录态 + 跳转登录页
 *
 * 网络错误（后端不可达 / 超时 / DNS 失败）：
 * - 不弹 toast（避免一页多接口同时失败时的 toast 风暴）
 * - 仅 console.warn
 * - reject 出去的 error 会带 `isNetworkError = true` 标志，业务页面据此渲染错误态
 */
const instance = axios.create({
  baseURL: '',
  timeout: 15000,
})

// 请求拦截：注入 token
instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = storage.getToken()
    if (token) {
      config.headers = config.headers ?? {}
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

// 响应拦截：解包 + 统一错误处理
instance.interceptors.response.use(
  (response: AxiosResponse<ApiResult>) => {
    const body = response.data
    if (!body || typeof body !== 'object' || !('code' in body)) {
      // 后端返回非标准结构（如 actuator）
      return response.data as unknown as never
    }
    if (body.code === ResultCode.OK) {
      return body.data as never
    }
    handleBusinessError(body.code, body.message, response.config)
    return Promise.reject(new BusinessError(body.code, body.message))
  },
  (error: AxiosError<ApiResult>) => {
    const body = error.response?.data
    if (body && typeof body === 'object' && 'code' in body) {
      handleBusinessError(body.code, body.message, error.config)
      return Promise.reject(new BusinessError(body.code, body.message))
    }
    if (!error.response) {
      // 网络不可达 / 超时：不弹 toast，挂标志让业务接管
      const reason =
        error.code === 'ECONNABORTED' ? 'timeout' : (error.code ?? 'NETWORK_ERROR')
      console.warn('[Network error]', reason, error.message)
      const netErr = new NetworkError(
        error.code === 'ECONNABORTED' ? '请求超时' : '后端服务不可达',
        reason
      )
      return Promise.reject(netErr)
    }
    // 4xx / 5xx 且没有标准 body
    const status = error.response.status
    if (status >= 500) {
      ElMessage.error(`服务异常（${status}），请稍后重试`)
    } else if (status === 401) {
      handleBusinessError(ResultCode.AUTH_EXPIRED, '登录已失效', error.config)
    } else {
      ElMessage.error(`请求失败：${error.message}`)
    }
    return Promise.reject(error)
  }
)

export class BusinessError extends Error {
  constructor(
    public code: number,
    message: string
  ) {
    super(message)
    this.name = 'BusinessError'
  }
}

/** 网络层错误（后端不可达 / 超时 / DNS 失败）。业务页面应展示 ErrorState 而非 EmptyState。 */
export class NetworkError extends Error {
  readonly isNetworkError = true
  constructor(
    message: string,
    public reason: string
  ) {
    super(message)
    this.name = 'NetworkError'
  }
}

/** 业务页面便捷判断：error 是否为网络层错误。 */
export function isNetworkError(e: unknown): e is NetworkError {
  return !!e && typeof e === 'object' && (e as { isNetworkError?: boolean }).isNetworkError === true
}

function handleBusinessError(
  code: number,
  message: string,
  config?: { method?: string; headers?: unknown },
) {
  if (code === ResultCode.AUTH_EXPIRED) {
    // 游客（请求没带 token）不是"登录失效"，别踢去登录页：
    //   · 后台静默加载的 GET（列表、状态）→ 什么都不做，页面照常浏览
    //   · 主动操作（POST/PUT/DELETE）→ 只提示，由页面自己决定是否引导登录
    // 否则任何一个漏在白名单外的公开接口都会让游客「看文章就被要求登录」。
    const headers = config?.headers as Record<string, unknown> | undefined
    const hadToken = !!headers?.['Authorization']
    if (!hadToken) {
      if ((config?.method ?? 'get').toLowerCase() !== 'get') {
        ElMessage.warning('请先登录')
      }
      return
    }
    storage.clearToken()
    storage.clearUserInfo()
    ElMessage.error('登录已失效，请重新登录')
    setTimeout(() => {
      const currentPath = window.location.pathname + window.location.search
      window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`
    }, 800)
    return
  }
  if (code === ResultCode.ACCOUNT_DISABLED) {
    storage.clearToken()
    storage.clearUserInfo()
    ElMessage.error(message || '账号已被禁用')
    return
  }
  ElMessage.error(message || '操作失败')
}

export default instance
